/* Feb-2026 · Getting Started Wizard — the true first-run experience.
 * Fires once, right after a brand-new writer picks a coaching mode in
 * OnboardingPromptModeDialog (see `getting-started-wizard-open`/`-done`
 * events there). Four short steps: tell us about your story → pick a
 * starting point → write Chapter 1 → you're ready. Existing users
 * never see this — it resolves instantly and gets out of the way.
 */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Sparkles, ListOrdered, PenTool, Loader2, ArrowRight, ArrowLeft, Check,
} from 'lucide-react';
import { usePW } from '@/lib/store';
import { GENRES } from '@/lib/data';
import { hasEverChosenPromptMode } from '../OnboardingPromptModeDialog';

const STEP_TITLES = [
  'Tell us about your story',
  'Pick a starting point',
  'Write your first chapter',
  "You're ready",
];

const START_CHOICES = [
  {
    id: 'idea',
    icon: Sparkles,
    title: 'Start from my idea',
    body: 'Claude turns your one-line idea into Chapter 1.',
  },
  {
    id: 'outline',
    icon: ListOrdered,
    title: 'Follow a simple outline',
    body: 'Setup, Confrontation, Resolution — the classic three-part shape.',
  },
  {
    id: 'blank',
    icon: PenTool,
    title: 'Just start writing',
    body: 'Skip the planning — open a blank page right now.',
  },
];

function markResolved() {
  try { window.localStorage.setItem('pw_getting_started_wizard_done', '1'); } catch { /* ignore */ }
}

export default function GettingStartedWizard({ togglePanel }) {
  const {
    project, chapters, updateProject, generateChapter,
    streamingChapterId, streamingText,
  } = usePW();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [title, setTitleField] = useState('');
  const [genre, setGenre] = useState('');
  const [idea, setIdea] = useState('');
  const [startChoice, setStartChoice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [didGenerate, setDidGenerate] = useState(false);
  const [generatedWords, setGeneratedWords] = useState(0);

  // iter226 — `onOpen` only runs once (on the 'getting-started-wizard-open'
  // event), which can fire well after mount if the project is still
  // loading. Read the LATEST project through a ref so the prefill isn't
  // stuck with whatever `project` happened to be at first render.
  const projectRef = useRef(project);
  projectRef.current = project;

  // Resolve instantly for anyone who isn't a true first-timer; only
  // truly new users wait for the 'getting-started-wizard-open' event
  // (dispatched by OnboardingPromptModeDialog right after their very
  // first coaching-mode pick). `pw_getting_started_wizard_done` is the
  // definitive "have we shown/resolved this" marker other code can
  // check synchronously (e.g. WelcomeOverlay) without depending on the
  // prompt-mode internals.
  useEffect(() => {
    if (hasEverChosenPromptMode()) {
      markResolved();
      return undefined;
    }
    const onOpen = () => {
      const p = projectRef.current;
      setTitleField(p?.name || '');
      setGenre(p?.genre || '');
      setStep(1);
      setOpen(true);
    };
    window.addEventListener('getting-started-wizard-open', onOpen);
    return () => window.removeEventListener('getting-started-wizard-open', onOpen);
  }, []);

  const finish = () => {
    if (generating) return; // don't abandon an in-flight AI draft
    setOpen(false);
    markResolved();
    // Land the brand-new writer straight in the editor — Home (the
    // Task Launcher) is the default first screen, but this wizard's
    // whole purpose is getting them onto the page.
    togglePanel?.('home', false);
    togglePanel?.('editor', true);
    window.dispatchEvent(new CustomEvent('getting-started-wizard-done'));
  };

  const firstChapter = (chapters || [])[0] || null;
  const firstChapterHasContent = !!(firstChapter?.content || '').trim();
  const isStreamingFirst = firstChapter && streamingChapterId === firstChapter.id;
  // Someone landed on step 3 with 'idea' picked, but their project
  // already has real Chapter 1 content (e.g. a returning account whose
  // onboarding flags got reset) — never offer to overwrite it with AI.
  const canOfferAiDraft = startChoice === 'idea' && idea.trim() && !firstChapterHasContent;

  const handleStep1Next = async (skip) => {
    if (skip) { setStep(2); return; }
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;
    setSaving(true);
    try {
      await updateProject({ name: trimmedTitle, genre: genre || undefined, synopsis: idea.trim() || undefined });
    } catch {
      toast.error("Couldn't save your story details — you can edit them later from Setup.");
    }
    setSaving(false);
    setStep(2);
  };

  const handleGenerateFirstChapter = async () => {
    if (!firstChapter) { setStep(4); return; }
    setGenerating(true);
    try {
      const result = await generateChapter(firstChapter.id, idea.trim());
      if (result?.success) {
        setGeneratedWords(result.words || 0);
        setDidGenerate(true);
      } else {
        toast.error("Chapter 1 didn't save — you can try again from the editor, or just start writing.");
      }
    } catch {
      toast.error("Chapter 1 didn't save — you can try again from the editor, or just start writing.");
    }
    setGenerating(false);
    setStep(4);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) finish(); }}>
      <DialogContent overlayClassName="bg-transparent pointer-events-none" className="max-w-lg" data-testid="getting-started-wizard">
        <DialogHeader>
          <div className="mb-1 flex items-center gap-1.5" data-testid="wizard-step-progress">
            {[1, 2, 3, 4].map((n) => (
              <span
                key={n}
                className={`h-1 flex-1 rounded-full transition-colors ${n <= step ? 'bg-primary' : 'bg-muted'}`}
              />
            ))}
          </div>
          <DialogTitle>{STEP_TITLES[step - 1]}</DialogTitle>
          <DialogDescription>Step {step} of 4 — this only takes a minute.</DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3" data-testid="wizard-step-1">
            <div>
              <label className="mb-1 block text-[12px] font-medium text-foreground">Story title</label>
              <Input
                value={title}
                onChange={(e) => setTitleField(e.target.value)}
                placeholder="My First Novel"
                data-testid="wizard-title-input"
              />
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium text-foreground">Genre</label>
              <select
                value={genre}
                onChange={(e) => setGenre(e.target.value)}
                data-testid="wizard-genre-select"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-[13px]"
              >
                <option value="">Pick one (optional)</option>
                {GENRES.map((g) => (
                  <option key={g.id} value={g.id}>{g.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium text-foreground">
                Your idea, in one line <span className="font-normal text-muted-foreground">(optional)</span>
              </label>
              <Textarea
                value={idea}
                onChange={(e) => setIdea(e.target.value)}
                placeholder="A lighthouse keeper discovers a map that shouldn't exist…"
                rows={2}
                data-testid="wizard-idea-input"
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-2" data-testid="wizard-step-2">
            {START_CHOICES.map((c) => {
              const Icon = c.icon;
              const selected = startChoice === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setStartChoice(c.id)}
                  data-testid={`wizard-start-choice-${c.id}`}
                  className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition ${
                    selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'
                  }`}
                >
                  <Icon className={`mt-0.5 h-4 w-4 flex-shrink-0 ${selected ? 'text-primary' : 'text-muted-foreground'}`} />
                  <span>
                    <span className="block text-[13px] font-semibold text-foreground">{c.title}</span>
                    <span className="block text-[12px] text-muted-foreground">{c.body}</span>
                  </span>
                  {selected && <Check className="ml-auto h-4 w-4 flex-shrink-0 text-primary" />}
                </button>
              );
            })}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3 text-center" data-testid="wizard-step-3">
            {canOfferAiDraft ? (
              <>
                <p className="text-[12.5px] text-muted-foreground">
                  Want Claude to draft Chapter 1 from your idea, or would you rather write it yourself?
                </p>
                {generating && (
                  <div className="flex flex-col items-center gap-2 py-4 text-muted-foreground" data-testid="wizard-generating">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    <p className="text-[12px]">Drafting Chapter 1…</p>
                    {streamingText && isStreamingFirst && (
                      <p className="line-clamp-3 max-h-16 overflow-hidden text-[11px] italic text-muted-foreground/80">
                        {streamingText.slice(-220)}
                      </p>
                    )}
                  </div>
                )}
                {!generating && (
                  <div className="flex flex-wrap justify-center gap-2">
                    <Button onClick={handleGenerateFirstChapter} data-testid="wizard-generate-chapter-btn">
                      <Sparkles className="mr-1.5 h-3.5 w-3.5" /> Draft Chapter 1 for me
                    </Button>
                    <Button variant="outline" onClick={() => setStep(4)} data-testid="wizard-write-myself-btn">
                      I'll write it myself
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="text-[12.5px] text-muted-foreground">
                  {firstChapterHasContent
                    ? 'Chapter 1 already has some writing in it — pick up right where you left off.'
                    : 'Chapter 1 is ready and waiting for you.'}
                </p>
                <Button onClick={() => setStep(4)} data-testid="wizard-go-to-chapter1-btn">
                  Let's go <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                </Button>
              </>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3 py-2 text-center" data-testid="wizard-step-4">
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <Check className="h-5 w-5 text-primary" />
            </div>
            <p className="text-[13px] font-medium text-foreground">
              {didGenerate && generatedWords
                ? `Nice start — Chapter 1 has about ${generatedWords.toLocaleString()} words.`
                : 'Your page is ready whenever you are.'}
            </p>
            <p className="text-[12px] text-muted-foreground">
              Need a refresher later? Open the Quickstart Guide from the toolbar anytime.
            </p>
            <Button onClick={finish} data-testid="wizard-start-writing-btn">
              Start writing <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
            </Button>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-border pt-3">
          <div>
            {step > 1 && step < 4 && (
              <Button variant="ghost" size="sm" onClick={() => setStep((s) => s - 1)} disabled={generating} data-testid="wizard-back-btn">
                <ArrowLeft className="mr-1 h-3.5 w-3.5" /> Back
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step < 4 && (
              <button type="button" onClick={finish} disabled={generating} className="text-[11.5px] text-muted-foreground underline-offset-2 hover:underline disabled:opacity-40 disabled:cursor-not-allowed" data-testid="wizard-skip-btn">
                Skip
              </button>
            )}
            {step === 1 && (
              <Button size="sm" onClick={() => handleStep1Next(false)} disabled={saving || !title.trim()} data-testid="wizard-step1-next-btn">
                {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null} Next <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            )}
            {step === 2 && (
              <Button size="sm" onClick={() => setStep(3)} disabled={!startChoice} data-testid="wizard-step2-next-btn">
                Next <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
